import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useState, useEffect, useMemo } from "react";
import { Link } from "@/lib/react-router-dom-shim";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { SchemaOrg, getOrganizationSchema, getWebSiteSchema, getBreadcrumbSchema } from "@/components/seo/SchemaOrg";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Building2, MapPin, TrendingUp, Search, Star, ChevronRight } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";

const ITEMS_PER_PAGE = 24;

interface HotelEntry {
  slug: string;
  name: string;
  city: string | null;
  country: string | null;
  star_rating: number | null;
  visit_count: number;
  thumb_url: string | null;
}

interface CityGroup {
  city: string;
  hotels: HotelEntry[];
}

const HotelDirectory = () => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  
  const [hotels, setHotels] = useState<HotelEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'popular' | 'city'>('popular');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      const { data, error } = await supabase
        .from('hotel_static_pages')
        .select('slug, name, city, country, star_rating, visit_count, thumb_url')
        .order('visit_count', { ascending: false });

      if (error) throw error;
      setHotels(data || []);
    } catch (error) {
      console.error('Error fetching hotels:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter hotels by search query
  const filteredHotels = useMemo(() => {
    if (!searchQuery.trim()) return hotels;
    const query = searchQuery.toLowerCase();
    return hotels.filter(h => 
      h.name.toLowerCase().includes(query) || 
      h.city?.toLowerCase().includes(query)
    );
  }, [hotels, searchQuery]);

  // Group hotels by city
  const hotelsByCity = useMemo(() => {
    const groups: Record<string, HotelEntry[]> = {};
    filteredHotels.forEach(hotel => {
      const city = hotel.city || t('hotelDirectory.unknownCity');
      if (!groups[city]) groups[city] = [];
      groups[city].push(hotel);
    });
    
    // Sort cities by total visit count
    return Object.entries(groups)
      .map(([city, cityHotels]) => ({
        city,
        hotels: cityHotels.sort((a, b) => b.visit_count - a.visit_count)
      }))
      .sort((a, b) => {
        const aVisits = a.hotels.reduce((sum, h) => sum + h.visit_count, 0);
        const bVisits = b.hotels.reduce((sum, h) => sum + h.visit_count, 0);
        return bVisits - aVisits;
      });
  }, [filteredHotels, t]);

  // Paginated hotels for popular tab
  const totalPages = Math.ceil(filteredHotels.length / ITEMS_PER_PAGE);
  
  const paginatedHotels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHotels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredHotels, currentPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // SEO
  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home'), url: baseUrl },
    { name: t('hotelDirectory.title') }
  ]);

  const HotelCard = ({ hotel }: { hotel: HotelEntry }) => (
    <Link 
      to={`/hotel/${hotel.slug}`}
      className="group block"
    >
      <Card className="h-full overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary/30">
        <CardContent className="p-0">
          {/* Image */}
          <div className="relative h-32 bg-muted overflow-hidden">
            {hotel.thumb_url ? (
              <img 
                src={hotel.thumb_url} 
                alt={hotel.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            {hotel.star_rating && (
              <div className="absolute top-2 right-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-md flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <span className="text-xs font-medium">{hotel.star_rating}</span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-3">
            <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
              {hotel.name}
            </h3>
            {hotel.city && (
              <div className="flex items-center gap-1 mt-1 text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <span className="text-xs">{hotel.city}</span>
              </div>
            )}
            {hotel.visit_count > 0 && (
              <div className="flex items-center gap-1 mt-1 text-muted-foreground">
                <TrendingUp className="h-3 w-3" />
                <span className="text-xs">{hotel.visit_count} {t('hotelDirectory.views')}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  const CitySection = ({ cityGroup }: { cityGroup: CityGroup }) => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          {cityGroup.city}
          <span className="text-sm font-normal text-muted-foreground">
            ({cityGroup.hotels.length} {t('hotelDirectory.hotels')})
          </span>
        </h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {cityGroup.hotels.slice(0, 12).map(hotel => (
          <HotelCard key={hotel.slug} hotel={hotel} />
        ))}
      </div>
      {cityGroup.hotels.length > 12 && (
        <div className="mt-4 text-center">
          <Button variant="outline" size="sm">
            {t('hotelDirectory.viewAllInCity', { city: cityGroup.city, count: cityGroup.hotels.length })}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <HreflangTags path="/directorio-hoteles" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema]} />
      
      <Navbar />
      
      <main id="main-content" className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-card">
          <div className="container mx-auto px-4 py-6">
            <Breadcrumb
              items={[
                { label: t('nav.home'), href: "/" },
                { label: t('hotelDirectory.title') },
              ]}
            />
            
            <div className="mt-4">
              <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-2">
                {t('hotelDirectory.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('hotelDirectory.subtitle', { count: hotels.length })}
              </p>
            </div>

            {/* Search */}
            <div className="mt-6 max-w-md" role="search" aria-label="Buscar hoteles en directorio">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Input
                  type="search"
                  placeholder={t('hotelDirectory.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  aria-label={t('hotelDirectory.searchPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-8">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array.from({ length: 18 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-32 w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : hotels.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">{t('hotelDirectory.noHotels')}</h2>
              <p className="text-muted-foreground">{t('hotelDirectory.noHotelsDescription')}</p>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'popular' | 'city')}>
              <TabsList className="mb-6">
                <TabsTrigger value="popular" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  {t('hotelDirectory.popularTab')}
                </TabsTrigger>
                <TabsTrigger value="city" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {t('hotelDirectory.byCityTab')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="popular">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {paginatedHotels.map(hotel => (
                    <HotelCard key={hotel.slug} hotel={hotel} />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex flex-col items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {t('hotelDirectory.showingOf', { 
                        showing: Math.min(currentPage * ITEMS_PER_PAGE, filteredHotels.length), 
                        total: filteredHotels.length 
                      })}
                    </p>
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        
                        {/* First page */}
                        {currentPage > 2 && (
                          <>
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(1)} className="cursor-pointer">
                                1
                              </PaginationLink>
                            </PaginationItem>
                            {currentPage > 3 && <PaginationEllipsis />}
                          </>
                        )}
                        
                        {/* Pages around current */}
                        {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(currentPage - 1 + i, totalPages - 2));
                          if (page < 1 || page > totalPages) return null;
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink 
                                onClick={() => setCurrentPage(page)}
                                isActive={currentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        
                        {/* Last page */}
                        {currentPage < totalPages - 1 && (
                          <>
                            {currentPage < totalPages - 2 && <PaginationEllipsis />}
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(totalPages)} className="cursor-pointer">
                                {totalPages}
                              </PaginationLink>
                            </PaginationItem>
                          </>
                        )}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="city">
                {hotelsByCity.map(cityGroup => (
                  <CitySection key={cityGroup.city} cityGroup={cityGroup} />
                ))}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default HotelDirectory;
